import { useEffect, useState } from "react";
import { Sheet } from "@/components/shell/Sheet";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { MoneyText } from "@/components/ui/MoneyText";
import { PhotoCapture } from "@/components/chores/PhotoCapture";
import { MascotMoment } from "@/components/brand/MascotMoment";
import { useAuth } from "@/lib/auth/context";
import { useMembers } from "@/lib/hooks/useMembers";
import { createCompletion } from "@/lib/db/completions";
import { notifyUsers } from "@/lib/api/notify";
import { formatPLN } from "@/lib/utils/money";
import { getXP } from "@/lib/utils/xp";
import { sportUnitLabel } from "@/lib/utils/sport";
import { genderFromRelation } from "@/lib/utils/roles";
import { toast } from "@/components/shell/Toaster";
import type { Chore, ChoreKind } from "@/lib/schemas";
import type { CompressedPhoto } from "@/lib/storage/proofPhotos";

type Props = {
  chore: Chore | null;
  onClose: () => void;
};

/**
 * Współdzielony sheet zgłoszenia chore. Używany w Chores.tsx i ChildDashboard.
 *
 * - Jeśli chore.requiresPhoto → renderuje PhotoCapture i blokuje submit
 *   dopóki zdjęcie nie zostanie wybrane
 * - Po sukcesie:
 *   - notify parentów z action buttons (Zatwierdź/Odrzuć z notyfikacji)
 *   - jeśli było zdjęcie — fire-and-forget AI verification
 *   - toast + zamknięcie
 */
export function CompletionSubmitSheet({ chore, onClose }: Props) {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const { members } = useMembers(hid);
  const [pendingPhoto, setPendingPhoto] = useState<CompressedPhoto | null>(null);
  const [busy, setBusy] = useState(false);
  // Sport: ile dziecko zrobiło. Prefill = chore.sportTarget (one-click "tak,
  // zrobiłem cel"). Dziecko może wpisać więcej (rekord) lub mniej (próba).
  const [sportDoneStr, setSportDoneStr] = useState<string>("");
  // Po sukcesie pokazujemy fullscreen "Brawo!" zamiast po prostu zamykać sheet.
  const [celebration, setCelebration] = useState<{
    rewardMinor: number;
    childName?: string;
    female: boolean;
    choreKind: ChoreKind;
    offline: boolean;
  } | null>(null);

  // Prefill sportDone gdy sheet otwiera się dla sport chore.
  useEffect(() => {
    if (chore?.kind === "sport" && chore.sportTarget !== undefined) {
      setSportDoneStr(String(chore.sportTarget));
    } else {
      setSportDoneStr("");
    }
  }, [chore]);

  const close = () => {
    setPendingPhoto(null);
    setBusy(false);
    setCelebration(null);
    onClose();
  };

  const submit = async () => {
    if (!chore || !hid || !firebaseUser) return;
    if (chore.requiresPhoto && !pendingPhoto) {
      toast.error("Załącz zdjęcie-dowód");
      return;
    }
    let sportDone: number | undefined;
    if (chore.kind === "sport") {
      const n = Number(sportDoneStr);
      if (!sportDoneStr || !Number.isInteger(n) || n < 0) {
        toast.error("Wpisz ile zrobiłeś (liczba ≥ 0)");
        return;
      }
      sportDone = n;
    }
    setBusy(true);
    try {
      const completionId = await createCompletion(hid, firebaseUser.uid, chore, {
        proofPhotoBlob: pendingPhoto?.blob,
        ...(sportDone !== undefined ? { sportDone } : {}),
      });
      const me = members.find((m) => m.userId === firebaseUser.uid);
      const parentIds = members
        .filter((m) => m.role === "parent")
        .map((m) => m.userId);
      // Body notyfikacji: dostosowane do typu chore.
      const notifyTail =
        chore.kind === "family"
          ? "Pomoc rodzinie 💛"
          : chore.kind === "sport"
            ? `Sport 💪 (${sportDone ?? "?"} ${sportUnitLabel(chore.sportUnit)})`
            : `(${formatPLN(chore.rewardMinor)})`;
      void notifyUsers({
        userIds: parentIds,
        title: "Nowe zgłoszenie do zatwierdzenia",
        body: `${me?.displayName ?? "Dziecko"}: ${chore.title} ${notifyTail}`,
        url: "/approvals",
        actions: [
          { action: "approve", title: "Zatwierdź" },
          { action: "reject", title: "Odrzuć" },
        ],
        data: { completionId },
        // Bumpuje czerwoną liczbę na ikonie home-screen rodzica — backend
        // policzy aktualne pending już z nowym completion (Firestore consistency).
        badgeFromPending: true,
      });
      // Otwieramy mascot celebration zamiast plain toast — bardziej "reward-feel".
      setCelebration({
        rewardMinor: chore.rewardMinor,
        childName: me?.displayName,
        female: genderFromRelation(me?.relation) === "f",
        choreKind: chore.kind,
        offline: chore.offline,
      });
      // Zamknij sam sheet, modal zostaje aż user kliknie "Lecimy dalej!"
      setPendingPhoto(null);
      setBusy(false);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Błąd");
      setBusy(false);
    }
  };

  return (
    <>
    <Sheet
      open={!!chore}
      onClose={close}
      title="Zgłoś zrobione"
    >
      {chore && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl" aria-hidden>
              {chore.icon ?? "📝"}
            </span>
            <div>
              <p className="text-base font-semibold">{chore.title}</p>
              {chore.kind === "family" ? (
                <p className="text-xs text-muted">
                  Pomoc rodzinie 💛 · ⚡ +{getXP("family", chore.size)} XP (szybciej levelujesz)
                </p>
              ) : chore.kind === "sport" ? (
                <p className="text-xs text-muted">
                  Sport 💪 · Cel: {chore.sportTarget ?? "?"}{" "}
                  {sportUnitLabel(chore.sportUnit)} · ⚡ +
                  {getXP("sport", chore.size)} XP
                </p>
              ) : (
                <p className="text-xs text-muted">
                  Nagroda:{" "}
                  <MoneyText minor={chore.rewardMinor} tone="money" sign />
                </p>
              )}
            </div>
          </div>

          {chore.kind === "sport" && (
            <div>
              <Label htmlFor="sportDone">
                Ile zrobiłeś? ({sportUnitLabel(chore.sportUnit)})
              </Label>
              <Input
                id="sportDone"
                inputMode="numeric"
                value={sportDoneStr}
                onChange={(e) => setSportDoneStr(e.target.value)}
                placeholder={String(chore.sportTarget ?? "")}
                disabled={busy}
              />
              <p className="mt-1 text-[11px] text-muted">
                Cel: {chore.sportTarget ?? "?"}{" "}
                {sportUnitLabel(chore.sportUnit)}. Wpisz ile faktycznie zrobiłeś
                — możesz mniej albo więcej.
              </p>
            </div>
          )}

          {chore.requiresPhoto && (
            <PhotoCapture
              label="Wymagane: zrób zdjęcie pokazujące, że zadanie zostało wykonane."
              onPhotoReady={setPendingPhoto}
              onCleared={() => setPendingPhoto(null)}
              disabled={busy}
            />
          )}

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="secondary" onClick={close} disabled={busy}>
              Anuluj
            </Button>
            <Button
              variant="accent"
              onClick={submit}
              disabled={busy || (chore.requiresPhoto && !pendingPhoto)}
            >
              {busy ? "Wysyłam…" : "Wyślij ✓"}
            </Button>
          </div>
          <p className="text-[11px] text-center text-muted">
            {chore.kind === "family" || chore.kind === "sport"
              ? "Rodzic potwierdzi. Dostaniesz odznaki i XP."
              : "Rodzic potwierdzi i monety wpadną do skarbonki."}
          </p>
        </div>
      )}
    </Sheet>
    <MascotMoment
      context="task-submitted"
      open={!!celebration}
      onClose={() => setCelebration(null)}
      data={{
        rewardMinor: celebration?.rewardMinor,
        childName: celebration?.childName,
        choreKind: celebration?.choreKind,
        offline: celebration?.offline,
      }}
      female={celebration?.female ?? false}
    />
    </>
  );
}
